import {
	ChangeEvent,
	KeyboardEvent,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import {
	Avatar,
	Box,
	ButtonBase,
	CircularProgress,
	Divider,
	Drawer,
	Grid2,
	IconButton,
	SvgIcon,
	TextField,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import SideNavigationBar from '../../components/SideNavigationBar';
import ChatRow from '../../components/ChatRow';
import CustomSwitch from '../../components/CustomSwitch';
import ChatNumbersList from './ChatNumbersList';
import {
	BackArrowIcon,
	CloseIcon,
	SendChatIcon,
	SendLinkIcon,
} from '../../assets/icons/Icons';
import { GREY, MAIN_THEME_COLOR } from '../../assets/theme/Colors';
import { useMainCustomStyle } from '../../assets/theme/Theme';
import { ChatData, ChatList, Tag } from '../../types';
import {
	ConversationChannel,
	ConversationMessage,
	ConversationTranscript,
	ConversationUser,
	CONVERSATION_USERS_PAGE_SIZE,
	getConversationMedia,
	getConversationTranscript,
	getConversationUsers,
	sendConsultantDocument,
	sendConsultantMessage,
	sendConsultantVoice,
	updateConsultantMode,
} from '../../services/ConversationApi';

interface SelectedChat {
	phoneNumber: string;
	name: string;
	email: string;
	isConsultantNeeded: boolean;
}

interface UserPagination {
	total: number;
	nextSkip: number;
	hasMore: boolean;
}

const initialPagination: Record<ConversationChannel, UserPagination> = {
	whatsapp: { total: 0, nextSkip: 0, hasMore: true },
	website: { total: 0, nextSkip: 0, hasMore: true },
};

function VoiceIcon() {
	return (
		<SvgIcon fontSize="small" viewBox="0 0 24 24">
			<path d="M12 14a3 3 0 0 0 3-3V5a3 3 0 1 0-6 0v6a3 3 0 0 0 3 3Zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.92V21H8v2h8v-2h-3v-3.08A7 7 0 0 0 19 11h-2Z" />
		</SvgIcon>
	);
}

function CopyIcon() {
	return (
		<SvgIcon fontSize="small" viewBox="0 0 24 24">
			<path d="M16 1H4a2 2 0 0 0-2 2v14h2V3h12V1Zm3 4H8a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2Zm0 16H8V7h11v14Z" />
		</SvgIcon>
	);
}

function CheckIcon() {
	return (
		<SvgIcon fontSize="small" viewBox="0 0 24 24">
			<path d="m9 16.17-3.88-3.88L3.71 13.7 9 19l12-12-1.41-1.41L9 16.17Z" />
		</SvgIcon>
	);
}

function EmailCopyButton({ email }: { email: string }) {
	const [copiedUntil, setCopiedUntil] = useState(0);
	const copied = copiedUntil > 0;

	useEffect(() => {
		if (!copiedUntil) return;
		const timer = window.setTimeout(() => setCopiedUntil(0), 5000);
		return () => window.clearTimeout(timer);
	}, [copiedUntil]);

	useEffect(() => {
		setCopiedUntil(0);
	}, [email]);

	async function copyEmail() {
		try {
			if (navigator.clipboard?.writeText) {
				await navigator.clipboard.writeText(email);
			} else {
				const input = document.createElement('textarea');
				input.value = email;
				document.body.appendChild(input);
				input.select();
				const didCopy = document.execCommand('copy');
				input.remove();
				if (!didCopy) throw new Error('Copy failed.');
			}
			setCopiedUntil(Date.now());
		} catch {
			setCopiedUntil(0);
		}
	}

	return (
		<Box
			display="inline-flex"
			alignItems="center"
			gap={0.25}
			flexShrink={0}>
			<IconButton
				size="small"
				aria-label={copied ? 'Email copied' : 'Copy email'}
				title={copied ? 'Copied' : 'Copy email'}
				onClick={event => {
					event.stopPropagation();
					void copyEmail();
				}}
				sx={{
					width: 8,
					height: 8,
					p: 0.5,
					ml: 1,
					color: copied ? '#008069' : MAIN_THEME_COLOR,
					backgroundColor: copied ? '#D9FDD3' : 'transparent',
					transition:
						'background-color 180ms ease, color 180ms ease, transform 180ms ease',
					'&:hover': {
						backgroundColor: copied ? '#C7EFC0' : '#E7F8F2',
						transform: 'scale(1.08)',
					},
				}}>
				{copied ? <CheckIcon /> : <CopyIcon />}
			</IconButton>
			{copied && (
				<Typography
					component="span"
					aria-live="polite"
					fontSize="10px"
					fontWeight={700}
					color="#008069"
					sx={{
						whiteSpace: 'nowrap',
						display: { xs: 'none', sm: 'inline' },
					}}>
					Copied
				</Typography>
			)}
		</Box>
	);
}

function htmlToFormattedText(value: string) {
	if (!value) return '';
	const parser = new DOMParser();
	const body = parser.parseFromString(value, 'text/html').body;

	function serialize(node: Node): string {
		if (node.nodeType === Node.TEXT_NODE) return node.textContent || '';
		if (!(node instanceof HTMLElement)) return '';

		const content = Array.from(node.childNodes).map(serialize).join('');
		switch (node.tagName.toLowerCase()) {
			case 'br':
				return '\n';
			case 'p':
			case 'div':
				return `${content}\n`;
			case 'li':
				return `- ${content}\n`;
			case 'strong':
			case 'b':
				return `**${content}**`;
			case 'em':
			case 'i':
				return `*${content}*`;
			case 'code':
				return `\`${content}\``;
			case 'a': {
				const href = node.getAttribute('href') || '';
				return /^https?:\/\//i.test(href)
					? `[${content || href}](${href})`
					: content;
			}
			default:
				return content;
		}
	}

	return Array.from(body.childNodes)
		.map(serialize)
		.join('')
		.replace(/\n{3,}/g, '\n\n')
		.trim();
}

function parseApiDate(value: string) {
	const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value);
	return new Date(hasTimezone ? value : `${value}Z`);
}

function formatTime(value: string) {
	return parseApiDate(value).toLocaleTimeString([], {
		hour: '2-digit',
		minute: '2-digit',
	});
}

function getUserDetail(user: ConversationUser, field: string) {
	const value = user.user_details?.[field] ?? user.userdetails?.[field];
	return typeof value === 'string' ? value.trim() : '';
}

function getUserName(user: ConversationUser) {
	return getUserDetail(user, 'name');
}

function getUserDisplayName(user: ConversationUser) {
	const name = getUserName(user);
	const company = getUserDetail(user, 'company');
	return name ? (company ? `${name} - (${company})` : name) : '';
}

function getUserEmail(user?: ConversationUser) {
	return user ? getUserDetail(user, 'email') : '';
}

function getAvatarLabel(name: string) {
	return name.trim().charAt(0).toUpperCase() || 'V';
}

function toChatList(
	user: ConversationUser,
	channel: ConversationChannel,
): ChatList {
	const date = parseApiDate(user.updated_at);
	const datePart = date.toISOString().split('T')[0];
	const fallbackName =
		channel === 'whatsapp' ? 'WhatsApp visitor' : 'Website visitor';

	const name = getUserDisplayName(user) || fallbackName;

	return {
		name,
		phoneNumber: user.user_id,
		email: getUserEmail(user),
		timestamp: `${datePart} | ${formatTime(user.updated_at)}`,
		lastMessage: `${user.message_count} ${user.message_count === 1 ? 'message' : 'messages'}`,
		isConsultantNeeded: user.consultant_mode,
		unreadMessageCount: 0,
	};
}

function mergeConversationUsers(
	existing: ConversationUser[],
	incoming: ConversationUser[],
) {
	const byUserId = new Map(existing.map(user => [user.user_id, user]));
	incoming.forEach(user => byUserId.set(user.user_id, user));
	return Array.from(byUserId.values()).sort(
		(a, b) =>
			parseApiDate(b.updated_at).getTime() -
			parseApiDate(a.updated_at).getTime(),
	);
}

function toChatData(message: ConversationMessage): ChatData {
	return {
		type:
			message.role === 'user'
				? 'human'
				: message.role === 'consultant'
					? 'consultant'
					: 'bot',
		text: htmlToFormattedText(message.text || ''),
		time: '',
		date: '',
		fileName: message.media_id
			? message.filename || 'Attachment'
			: undefined,
		fileUrl:
			message.media_id && !message.expired
				? `${message.media_id}|${encodeURIComponent(message.filename || 'attachment')}`
				: undefined,
		caption: message.caption || '',
		isFileIsImage: false,
		imageURL: '',
		isThisReplyMsg: false,
		repliedMsg: '',
		isMsgHasReaction: false,
		reaction: '',
	};
}

interface ConversationBodyProps {
	transcript: ConversationTranscript | null;
	loading: boolean;
	error: string;
	message: string;
	selectedFile: File | null;
	isSending: boolean;
	isModeSaving: boolean;
	isDownloading: boolean;
	downloadingFile: string;
	onMessageChange: (value: string) => void;
	onSend: () => void;
	onModeChange: (event: ChangeEvent<HTMLInputElement>) => void;
	onDocumentSelected: (event: ChangeEvent<HTMLInputElement>) => void;
	onVoiceSelected: (event: ChangeEvent<HTMLInputElement>) => void;
	onCancelFile: () => void;
	onDownload: (url: string) => void;
}

function ConversationBody({
	transcript,
	loading,
	error,
	message,
	selectedFile,
	isSending,
	isModeSaving,
	isDownloading,
	downloadingFile,
	onMessageChange,
	onSend,
	onModeChange,
	onDocumentSelected,
	onVoiceSelected,
	onCancelFile,
	onDownload,
}: ConversationBodyProps) {
	const messagesRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (messagesRef.current) {
			messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
		}
	}, [transcript?.messages.length, transcript?.user_id]);

	if (loading && !transcript) {
		return (
			<Grid2
				container
				width="100%"
				height="100%"
				flexDirection="column"
				gap={1.5}
				justifyContent="center"
				alignItems="center"
				sx={{ backgroundColor: '#F4F7F5' }}>
				<CircularProgress size={28} sx={{ color: MAIN_THEME_COLOR }} />
				<Typography fontSize="12px" color="#667781">
					Loading messages
				</Typography>
			</Grid2>
		);
	}

	if (!transcript) {
		return (
			<Grid2
				container
				width="100%"
				height="100%"
				flexDirection="column"
				justifyContent="center"
				alignItems="center"
				sx={{ backgroundColor: '#F4F7F5', px: 3 }}>
				<Box
					sx={{
						width: 64,
						height: 64,
						borderRadius: '50%',
						backgroundColor: '#D9FDD3',
						color: MAIN_THEME_COLOR,
						display: 'grid',
						placeItems: 'center',
						fontSize: '22px',
						fontWeight: 700,
						mb: 2,
					}}>
					CM
				</Box>
				<Typography fontSize="17px" fontWeight={600}>
					No conversation selected
				</Typography>
				<Typography fontSize="12px" color="#667781" mt={0.5}>
					{error || 'Messages will appear here.'}
				</Typography>
			</Grid2>
		);
	}

	const isWhatsApp = transcript.channel === 'whatsapp';
	const canReply = isWhatsApp && transcript.consultant_mode;
	const chatData = transcript.messages.map(toChatData);
	const dateLabel = parseApiDate(transcript.updated_at)
		.toISOString()
		.split('T')[0];

	function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
		if (event.key === 'Enter' && !event.shiftKey) {
			event.preventDefault();
			onSend();
		}
	}

	return (
		<Grid2
			width="100%"
			sx={{
				height: '100%',
				minHeight: 0,
				display: 'flex',
				flexDirection: 'column',
			}}>
			<Box
				ref={messagesRef}
				sx={{
					overflowY: 'auto',
					flex: 1,
					minHeight: 0,
					width: '100%',
					backgroundColor: '#F4F7F5',
					'&::-webkit-scrollbar': { display: 'none' },
				}}>
				<Grid2 width="100%" sx={{ py: 1.5 }}>
					<Typography
						variant="body2"
						fontWeight={600}
						textAlign="center"
						sx={{
							width: 'fit-content',
							mx: 'auto',
							my: 1.25,
							px: 1.25,
							py: 0.45,
							fontSize: '10px',
							color: '#54656F',
							backgroundColor: '#FFFFFF',
							borderRadius: '5px',
							boxShadow: '0 1px 1px rgba(11, 20, 26, 0.08)',
						}}>
						{dateLabel}
					</Typography>
					{chatData.map((chat, index) => (
						<ChatRow
							key={index}
							position={chat.type === 'human' ? 'left' : 'right'}
							text={chat.text}
							chatTime={chat.time}
							fileName={chat.fileName}
							fileUrl={chat.fileUrl}
							onDownload={onDownload}
							caption={chat.caption}
							chatOwner={chat.type}
							isDownloading={isDownloading}
							downloadingFile={downloadingFile}
						/>
					))}
				</Grid2>
			</Box>

			{error && (
				<Grid2
					container
					sx={{ px: 2, py: 0.5, backgroundColor: '#FFF5F5' }}>
					<Typography fontSize="11px" color="#D32F2F">
						{error}
					</Typography>
				</Grid2>
			)}

			{selectedFile && (
				<Grid2
					container
					alignItems="center"
					justifyContent="space-between"
					sx={{
						mx: 1.5,
						mt: 1,
						px: 1.5,
						py: 1,
						backgroundColor: '#FFFFFF',
						border: '1px solid #E9EDEF',
						borderRadius: '6px',
					}}>
					<Box sx={{ minWidth: 0 }}>
						<Typography noWrap fontSize="12px" color="#292D32">
							{selectedFile.name}
						</Typography>
						<Typography fontSize="10px" color="#A9ACB4">
							{(selectedFile.size / 1024).toFixed(2)} KB
						</Typography>
					</Box>
					<IconButton size="small" onClick={onCancelFile}>
						<CloseIcon />
					</IconButton>
				</Grid2>
			)}

			<Grid2
				container
				width="100%"
				columnSpacing={1}
				alignItems="center"
				sx={{
					minHeight: 62,
					px: 1.25,
					py: 1,
					backgroundColor: '#F0F2F5',
					borderTop: '1px solid #E1E6E8',
				}}>
				<Grid2
					size={{ xs: isWhatsApp ? 9 : 12, md: isWhatsApp ? 9 : 12 }}
					sx={{
						display: 'flex',
						alignItems: 'center',
						minWidth: 0,
						minHeight: 44,
						px: 0.5,
						bgcolor: canReply ? '#FFFFFF' : '#E5E9EB',
						borderRadius: '22px',
					}}>
					<TextField
						variant="standard"
						multiline
						disabled={!canReply || isSending}
						placeholder={
							isWhatsApp
								? canReply
									? 'Type your message here...'
									: 'Turn on consultant mode to reply'
								: 'Website conversations are read-only'
						}
						sx={{ flex: 1, minWidth: 0 }}
						slotProps={{
							input: {
								disableUnderline: true,
								style: {
									paddingLeft: '10px',
									fontSize: '13px',
								},
							},
						}}
						onKeyDown={onKeyDown}
						value={message}
						onChange={event => onMessageChange(event.target.value)}
					/>
					{canReply && (
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								flexShrink: 0,
							}}>
							<IconButton
								component="label"
								aria-label="Attach document"
								sx={{ width: 36, height: 36 }}>
								<SendLinkIcon />
								<input
									hidden
									type="file"
									onChange={onDocumentSelected}
								/>
							</IconButton>
							<IconButton
								component="label"
								aria-label="Attach voice note"
								sx={{
									width: 36,
									height: 36,
									color: '#54656F',
								}}>
								<VoiceIcon />
								<input
									hidden
									type="file"
									accept=".ogg,audio/ogg,audio/opus"
									onChange={onVoiceSelected}
								/>
							</IconButton>
							{isSending ? (
								<CircularProgress
									sx={{ color: MAIN_THEME_COLOR, mx: 1 }}
									size={20}
								/>
							) : (
								<IconButton
									aria-label="Send message"
									disabled={!selectedFile && !message.trim()}
									onClick={onSend}
									sx={{
										width: 36,
										height: 36,
										'&:hover': {
											backgroundColor: '#E7F8F2',
										},
									}}>
									<SendChatIcon />
								</IconButton>
							)}
						</Box>
					)}
				</Grid2>

				{isWhatsApp && (
					<Grid2
						size={{ xs: 3, md: 3 }}
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							gap: 0.75,
							minHeight: 44,
						}}>
						<Typography
							fontSize="11px"
							fontWeight={600}
							color={
								transcript.consultant_mode
									? MAIN_THEME_COLOR
									: GREY
							}
							sx={{ display: { xs: 'none', sm: 'block' } }}>
							Consultant
						</Typography>
						{isModeSaving ? (
							<CircularProgress size={20} />
						) : (
							<CustomSwitch
								isChecked={transcript.consultant_mode}
								handleChange={onModeChange}
							/>
						)}
					</Grid2>
				)}
			</Grid2>
		</Grid2>
	);
}

interface ChatWindowProps extends ConversationBodyProps {
	selectedChat: SelectedChat;
	isMobile: boolean;
	onBack: () => void;
	onOpenDetails: () => void;
}

function ChatWindow({
	selectedChat,
	isMobile,
	onBack,
	onOpenDetails,
	...bodyProps
}: ChatWindowProps) {
	return (
		<Grid2
			component="main"
			size={{ xs: 12, sm: 8, xl: 9 }}
			sx={{ height: '100%', minWidth: 0 }}>
			<Grid2
				bgcolor="#FFFFFF"
				sx={{
					height: '100%',
					display: 'flex',
					flexDirection: 'column',
					minHeight: 0,
				}}>
				<Grid2
					container
					justifyContent="space-between"
					alignItems="center"
					wrap="nowrap"
					sx={{
						minHeight: 64,
						px: { xs: 1, sm: 2 },
						backgroundColor: '#F0F2F5',
						borderBottom: '1px solid #DDE2E4',
					}}>
					{selectedChat.phoneNumber && (
						<Grid2
							container
							alignItems="center"
							wrap="nowrap"
							sx={{ minWidth: 0 }}>
							{isMobile && (
								<IconButton
									aria-label="Back to conversations"
									onClick={onBack}
									sx={{ mr: 0.25 }}>
									<BackArrowIcon />
								</IconButton>
							)}
							<ButtonBase
								component="div"
								onClick={onOpenDetails}
								aria-label="Open user details"
								sx={{
									minWidth: 0,
									justifyContent: 'flex-start',
									borderRadius: '5px',
									p: 0.5,
								}}>
								<Avatar
									sx={{
										width: 40,
										height: 40,
										backgroundColor: '#D9E4E1',
										color: '#3B4A54',
										fontSize: '13px',
										fontWeight: 700,
									}}>
									{getAvatarLabel(selectedChat.name)}
								</Avatar>
								<Grid2
									sx={{
										minWidth: 0,
										pl: 1.25,
										textAlign: 'left',
									}}>
									<Typography
										noWrap
										fontWeight={600}
										color="#111B21"
										fontSize="14px">
										{selectedChat.name}
									</Typography>
									{selectedChat.email && (
										<Box
											display="flex"
											alignItems="center"
											gap={0.25}
											sx={{ minWidth: 0 }}>
											<Typography
												noWrap
												color="#667781"
												fontSize="11px"
												sx={{
													minWidth: 0,
													overflow: 'hidden',
													textOverflow: 'ellipsis',
												}}>
												{selectedChat.email}
											</Typography>
											<EmailCopyButton
												email={selectedChat.email}
											/>
										</Box>
									)}
								</Grid2>
							</ButtonBase>
						</Grid2>
					)}
					{selectedChat.phoneNumber && (
						<Grid2
							container
							alignItems="center"
							gap={0.75}
							sx={{ ml: 1, flexShrink: 0 }}>
							<Box
								sx={{
									width: 7,
									height: 7,
									borderRadius: '50%',
									backgroundColor: MAIN_THEME_COLOR,
								}}
							/>
							<Typography
								fontSize="11px"
								color="#667781"
								sx={{ display: { xs: 'none', sm: 'block' } }}>
								Live
							</Typography>
						</Grid2>
					)}
				</Grid2>
				<Grid2 container sx={{ flex: 1, minHeight: 0 }}>
					<ConversationBody {...bodyProps} />
				</Grid2>
			</Grid2>
		</Grid2>
	);
}

function formatDetailValue(key: string, value: unknown) {
	if (value === null || value === undefined || value === '') return '—';
	if (typeof value === 'boolean') return value ? 'Yes' : 'No';
	if (typeof value === 'object') return JSON.stringify(value, null, 2);
	if (typeof value === 'string' && /(?:_at|_since)$/.test(key)) {
		const date = parseApiDate(value);
		if (!Number.isNaN(date.getTime())) return date.toLocaleString();
	}
	return String(value);
}

function UserDetailsDrawer({
	open,
	onClose,
	user,
	transcript,
}: {
	open: boolean;
	onClose: () => void;
	user?: ConversationUser;
	transcript: ConversationTranscript | null;
}) {
	const details: Record<string, unknown> = { ...(user || {}) };
	if (transcript) {
		Object.entries(transcript).forEach(([key, value]) => {
			if (key !== 'messages') details[key] = value;
		});
	}
	delete details.user_details;
	delete details.userdetails;

	const profileName = user ? getUserName(user) : '';
	const profileDisplayName = user ? getUserDisplayName(user) : '';
	const email = getUserEmail(user);
	const profileFields = user
		? [
				{ label: 'Country', value: getUserDetail(user, 'country') },
				{ label: 'Phone', value: getUserDetail(user, 'phone') },
			].filter(field => field.value)
		: [];

	const preferredOrder = [
		'user_id',
		'wa_id',
		'channel',
		'created_at',
		'updated_at',
		'message_count',
		'consultant_mode',
		'consultant_since',
	];
	const hiddenDetailKeys = new Set([
		'user_id',
		'wa_id',
		'web_id',
		'created_at',
		'updated_at',
		'user_info',
	]);
	const keys = [
		...preferredOrder.filter(
			key => key in details && !hiddenDetailKeys.has(key),
		),
		...Object.keys(details).filter(
			key => !preferredOrder.includes(key) && !hiddenDetailKeys.has(key),
		),
	];
	const avatarLabel = getAvatarLabel(profileName);

	return (
		<Drawer
			anchor="right"
			open={open}
			onClose={onClose}
			PaperProps={{
				sx: {
					width: { xs: '100%', sm: 390 },
					maxWidth: '100%',
					top: '60px',
					height: 'calc(100% - 60px)',
				},
			}}>
			<Box
				sx={{
					position: 'relative',
					px: 2.5,
					py: 3,
					textAlign: 'center',
				}}>
				<IconButton
					aria-label="Close user details"
					title="Close"
					onClick={onClose}
					sx={{
						position: 'absolute',
						top: 10,
						right: 10,
						color: '#54656F',
					}}>
					<SvgIcon fontSize="small" viewBox="0 0 24 24">
						<path d="m18.3 5.71-1.41-1.42L12 9.17 7.11 4.29 5.7 5.71 10.59 10.59 5.7 15.48l1.41 1.41L12 12l4.89 4.89 1.41-1.41-4.89-4.89 4.89-4.88Z" />
					</SvgIcon>
				</IconButton>
				<Avatar
					sx={{
						width: 72,
						height: 72,
						mx: 'auto',
						mb: 1.25,
						backgroundColor: '#D9E4E1',
						color: '#3B4A54',
						fontWeight: 700,
					}}>
					{avatarLabel}
				</Avatar>
				<Typography fontSize="15px" fontWeight={700}>
					{profileDisplayName || 'Visitor'}
				</Typography>
				{email && (
					<Box
						display="flex"
						alignItems="center"
						justifyContent="center"
						gap={0.5}
						mt={0.3}>
						<Typography
							fontSize="11px"
							color="#667781"
							sx={{ wordBreak: 'break-word' }}>
							{email}
						</Typography>
						<EmailCopyButton email={email} />
					</Box>
				)}
			</Box>
			<Divider />
			{(profileName || profileFields.length > 0) && (
				<Box sx={{ px: 2.5, py: 1.5 }}>
					<Typography
						fontSize="10px"
						fontWeight={700}
						color="#667781"
						textTransform="uppercase"
						sx={{ mb: 0.5 }}>
						Contact
					</Typography>
					{profileName && (
						<Box sx={{ py: 1.25 }}>
							<Typography
								fontSize="10px"
								fontWeight={700}
								color="#667781"
								textTransform="uppercase">
								Name
							</Typography>
							<Typography
								fontSize="12px"
								color="#111B21"
								mt={0.35}>
								{profileDisplayName}
							</Typography>
						</Box>
					)}
					{profileFields.map(field => (
						<Box key={field.label} sx={{ py: 1.25 }}>
							<Typography
								fontSize="10px"
								fontWeight={700}
								color="#667781"
								textTransform="uppercase">
								{field.label}
							</Typography>
							<Typography
								fontSize="12px"
								color="#111B21"
								mt={0.35}>
								{field.value}
							</Typography>
						</Box>
					))}
				</Box>
			)}
			<Divider />
			<Box sx={{ px: 2.5, py: 1.5, overflowY: 'auto' }}>
				{keys.map(key => (
					<Box
						key={key}
						sx={{
							py: 1.25,
							borderBottom: '1px solid #F0F2F5',
						}}>
						<Typography
							fontSize="10px"
							fontWeight={700}
							color="#667781"
							textTransform="uppercase">
							{key.replace(/_/g, ' ')}
						</Typography>
						<Typography
							component="div"
							fontSize="12px"
							color="#111B21"
							mt={0.35}
							sx={{
								whiteSpace: 'pre-wrap',
								wordBreak: 'break-word',
							}}>
							{formatDetailValue(key, details[key])}
						</Typography>
					</Box>
				))}
			</Box>
		</Drawer>
	);
}

function Conversations() {
	const mainDivStyle = useMainCustomStyle();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const [channel, setChannel] = useState<ConversationChannel>('whatsapp');
	const [users, setUsers] = useState<
		Record<ConversationChannel, ConversationUser[]>
	>({ whatsapp: [], website: [] });
	const [pagination, setPagination] =
		useState<Record<ConversationChannel, UserPagination>>(
			initialPagination,
		);
	const [loadingUsers, setLoadingUsers] = useState(true);
	const [loadingMoreUsers, setLoadingMoreUsers] = useState<
		Record<ConversationChannel, boolean>
	>({ whatsapp: false, website: false });
	const [listError, setListError] = useState('');
	const [search, setSearch] = useState('');
	const [selectedChat, setSelectedChat] = useState<SelectedChat>({
		phoneNumber: '',
		name: '',
		email: '',
		isConsultantNeeded: false,
	});
	const [transcript, setTranscript] = useState<ConversationTranscript | null>(
		null,
	);
	const [loadingTranscript, setLoadingTranscript] = useState(false);
	const [chatError, setChatError] = useState('');
	const [message, setMessage] = useState('');
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [selectedFileKind, setSelectedFileKind] = useState<
		'document' | 'voice'
	>('document');
	const [sending, setSending] = useState(false);
	const [modeSaving, setModeSaving] = useState(false);
	const [downloading, setDownloading] = useState(false);
	const [downloadingFile, setDownloadingFile] = useState('');
	const [showNumberList, setShowNumberList] = useState(true);
	const [consultantOnly, setConsultantOnly] = useState(false);
	const [detailsOpen, setDetailsOpen] = useState(false);
	const loadingMoreUsersRef = useRef<Record<ConversationChannel, boolean>>({
		whatsapp: false,
		website: false,
	});

	const refreshUsers = useCallback(async (showLoading = true) => {
		if (showLoading) setLoadingUsers(true);
		try {
			const [whatsapp, website] = await Promise.all([
				getConversationUsers('whatsapp'),
				getConversationUsers('website'),
			]);
			setUsers(current => ({
				whatsapp: mergeConversationUsers(
					current.whatsapp,
					whatsapp.users,
				),
				website: mergeConversationUsers(current.website, website.users),
			}));
			setPagination(current => {
				const update = (
					channel: ConversationChannel,
					pageSize: number,
					total: number,
				): UserPagination => {
					const nextSkip = current[channel].nextSkip || pageSize;
					return {
						total,
						nextSkip,
						hasMore:
							nextSkip < total &&
							pageSize === CONVERSATION_USERS_PAGE_SIZE,
					};
				};

				return {
					whatsapp: update(
						'whatsapp',
						whatsapp.users.length,
						whatsapp.total,
					),
					website: update(
						'website',
						website.users.length,
						website.total,
					),
				};
			});
			setListError('');
		} catch (error) {
			setListError(
				error instanceof Error
					? error.message
					: 'Could not load conversations.',
			);
		} finally {
			if (showLoading) setLoadingUsers(false);
		}
	}, []);

	const loadMoreUsers = useCallback(async () => {
		const currentPagination = pagination[channel];
		if (
			!currentPagination.hasMore ||
			loadingMoreUsersRef.current[channel]
		) {
			return;
		}

		loadingMoreUsersRef.current[channel] = true;
		setLoadingMoreUsers(current => ({ ...current, [channel]: true }));
		try {
			const page = await getConversationUsers(channel, {
				limit: CONVERSATION_USERS_PAGE_SIZE,
				skip: currentPagination.nextSkip,
			});
			const nextSkip = currentPagination.nextSkip + page.users.length;
			setUsers(current => ({
				...current,
				[channel]: mergeConversationUsers(current[channel], page.users),
			}));
			setPagination(current => ({
				...current,
				[channel]: {
					total: page.total,
					nextSkip,
					hasMore:
						nextSkip < page.total &&
						page.users.length === CONVERSATION_USERS_PAGE_SIZE,
				},
			}));
			setListError('');
		} catch (error) {
			setListError(
				error instanceof Error
					? error.message
					: 'Could not load more conversations.',
			);
		} finally {
			loadingMoreUsersRef.current[channel] = false;
			setLoadingMoreUsers(current => ({ ...current, [channel]: false }));
		}
	}, [channel, pagination]);

	useEffect(() => {
		void refreshUsers();
		const interval = window.setInterval(
			() => void refreshUsers(false),
			20000,
		);
		return () => window.clearInterval(interval);
	}, [refreshUsers]);

	const loadTranscript = useCallback(
		async (userId: string, showLoading = true) => {
			if (!userId) return;
			if (showLoading) setLoadingTranscript(true);
			try {
				const data = await getConversationTranscript(userId);
				setTranscript(data);
				setChatError('');
			} catch (error) {
				setChatError(
					error instanceof Error
						? error.message
						: 'Could not load the conversation.',
				);
			} finally {
				if (showLoading) setLoadingTranscript(false);
			}
		},
		[],
	);

	useEffect(() => {
		if (!selectedChat.phoneNumber) return;
		void loadTranscript(selectedChat.phoneNumber);
		const interval = window.setInterval(
			() => void loadTranscript(selectedChat.phoneNumber, false),
			10000,
		);
		return () => window.clearInterval(interval);
	}, [loadTranscript, selectedChat.phoneNumber]);

	const allChatList = useMemo(
		() => users[channel].map(user => toChatList(user, channel)),
		[channel, users],
	);
	const chatList = useMemo(() => {
		const needle = search.trim().toLowerCase();
		return allChatList.filter(chat => {
			const matchesSearch =
				!needle ||
				`${chat.name} ${chat.email || ''} ${chat.phoneNumber}`
					.toLowerCase()
					.includes(needle);
			return (
				matchesSearch &&
				(!consultantOnly ||
					channel !== 'whatsapp' ||
					chat.isConsultantNeeded)
			);
		});
	}, [allChatList, channel, consultantOnly, search]);
	const channelTags: Tag[] = [
		{ name: 'WhatsApp', count: pagination.whatsapp.total },
		{ name: 'Website', count: pagination.website.total },
	];

	function selectChannel(value: string) {
		setChannel(value === 'Website' ? 'website' : 'whatsapp');
		setSearch('');
		setSelectedChat({
			phoneNumber: '',
			name: '',
			email: '',
			isConsultantNeeded: false,
		});
		setTranscript(null);
		setDetailsOpen(false);
	}

	function selectChat(chat: SelectedChat) {
		setSelectedChat(chat);
		setTranscript(null);
		setChatError('');
		setMessage('');
		setSelectedFile(null);
		setShowNumberList(false);
		setDetailsOpen(false);
	}

	async function changeMode(event: ChangeEvent<HTMLInputElement>) {
		if (!transcript) return;
		setModeSaving(true);
		try {
			const result = await updateConsultantMode(
				transcript.user_id,
				event.target.checked,
			);
			setTranscript(current =>
				current
					? {
							...current,
							consultant_mode: result.enabled,
							consultant_since: result.since,
						}
					: current,
			);
			setUsers(current => ({
				...current,
				whatsapp: current.whatsapp.map(user =>
					user.user_id === transcript.user_id
						? { ...user, consultant_mode: result.enabled }
						: user,
				),
			}));
			setChatError('');
		} catch (error) {
			setChatError(
				error instanceof Error
					? error.message
					: 'Could not update consultant mode.',
			);
		} finally {
			setModeSaving(false);
		}
	}

	function chooseFile(
		event: ChangeEvent<HTMLInputElement>,
		kind: 'document' | 'voice',
	) {
		const file = event.target.files?.[0];
		event.target.value = '';
		if (!file) return;
		if (kind === 'document' && file.size > 100 * 1024 * 1024) {
			setChatError('Documents must be 100 MB or smaller.');
			return;
		}
		if (kind === 'voice') {
			const isOgg =
				file.type === 'audio/ogg' ||
				file.type === 'audio/opus' ||
				file.name.toLowerCase().endsWith('.ogg');
			if (!isOgg) {
				setChatError('Voice notes must be OGG/Opus files.');
				return;
			}
			if (file.size > 16 * 1024 * 1024) {
				setChatError('Voice notes must be 16 MB or smaller.');
				return;
			}
		}
		setSelectedFileKind(kind);
		setSelectedFile(file);
		setChatError('');
	}

	async function sendReply() {
		if (!transcript || sending || (!message.trim() && !selectedFile))
			return;
		setSending(true);
		try {
			if (selectedFile) {
				if (selectedFileKind === 'voice') {
					await sendConsultantVoice(transcript.user_id, selectedFile);
				} else {
					await sendConsultantDocument(
						transcript.user_id,
						selectedFile,
						message.trim(),
					);
				}
			} else {
				await sendConsultantMessage(transcript.user_id, message.trim());
			}
			setMessage('');
			setSelectedFile(null);
			setChatError('');
			await loadTranscript(transcript.user_id, false);
		} catch (error) {
			setChatError(
				error instanceof Error
					? error.message
					: 'Could not send the message.',
			);
		} finally {
			setSending(false);
		}
	}

	async function downloadMedia(url: string) {
		const [mediaId, encodedName] = url.split('|');
		const filename = decodeURIComponent(encodedName || 'attachment');
		setDownloading(true);
		setDownloadingFile(url);
		try {
			const blob = await getConversationMedia(mediaId, filename);
			const objectUrl = URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = objectUrl;
			link.download = filename;
			document.body.appendChild(link);
			link.click();
			link.remove();
			URL.revokeObjectURL(objectUrl);
		} catch (error) {
			setChatError(
				error instanceof Error
					? error.message
					: 'Could not download the file.',
			);
		} finally {
			setDownloading(false);
			setDownloadingFile('');
		}
	}

	const bodyProps: ConversationBodyProps = {
		transcript,
		loading: loadingTranscript,
		error: chatError,
		message,
		selectedFile,
		isSending: sending,
		isModeSaving: modeSaving,
		isDownloading: downloading,
		downloadingFile,
		onMessageChange: value => {
			setMessage(value);
			setChatError('');
		},
		onSend: () => void sendReply(),
		onModeChange: event => void changeMode(event),
		onDocumentSelected: event => chooseFile(event, 'document'),
		onVoiceSelected: event => chooseFile(event, 'voice'),
		onCancelFile: () => setSelectedFile(null),
		onDownload: url => void downloadMedia(url),
	};

	function renderList() {
		return (
			<ChatNumbersList
				chatListData={chatList}
				onSearchChatList={setSearch}
				onViewRecentChat={selectChat}
				selectedPhoneNumber={selectedChat.phoneNumber}
				loading={loadingUsers}
				onChangeGridVisibility={() => undefined}
				tagList={channelTags}
				onSelectTag={selectChannel}
				selectedTag={channel === 'whatsapp' ? 'WhatsApp' : 'Website'}
				searchedText={search}
				onLoadMore={loadMoreUsers}
				hasMore={pagination[channel].hasMore}
				loadingMore={loadingMoreUsers[channel]}
				showConsultantFilter={channel === 'whatsapp'}
				consultantOnly={consultantOnly}
				onConsultantOnlyChange={setConsultantOnly}
			/>
		);
	}

	function renderChat() {
		return (
			<ChatWindow
				{...bodyProps}
				selectedChat={selectedChat}
				isMobile={isMobile}
				onBack={() => setShowNumberList(true)}
				onOpenDetails={() => setDetailsOpen(true)}
			/>
		);
	}

	return (
		<>
			<SideNavigationBar />
			<div style={mainDivStyle}>
				{isMobile ? (
					<Grid2
						container
						height="100%"
						width="100%"
						sx={{ overflow: 'hidden', backgroundColor: '#FFFFFF' }}>
						{showNumberList ? renderList() : renderChat()}
					</Grid2>
				) : (
					<Grid2
						container
						height="100%"
						width="100%"
						sx={{
							p: { md: 1.5, lg: 2 },
							backgroundColor: '#F0F2F5',
						}}>
						<Grid2
							container
							height="100%"
							width="100%"
							sx={{
								overflow: 'hidden',
								backgroundColor: '#FFFFFF',
								border: '1px solid #DDE2E4',
								borderRadius: '6px',
							}}>
							{renderList()}
							{renderChat()}
						</Grid2>
					</Grid2>
				)}
			</div>
			<UserDetailsDrawer
				open={detailsOpen}
				onClose={() => setDetailsOpen(false)}
				user={users[channel].find(
					user => user.user_id === selectedChat.phoneNumber,
				)}
				transcript={transcript}
			/>
			{listError &&
				!loadingUsers &&
				users.whatsapp.length === 0 &&
				users.website.length === 0 && (
					<Box
						sx={{
							position: 'fixed',
							left: { xs: 16, sm: 232 },
							bottom: 16,
							px: 2,
							py: 1,
							borderRadius: '5px',
							backgroundColor: '#FFF5F5',
							color: '#D32F2F',
							fontSize: '12px',
						}}>
						{listError}
					</Box>
				)}
		</>
	);
}

export default Conversations;
